import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useWatch } from 'react-hook-form';

import { DateRangePicker } from '@/components/common/date-picker';
import { Drawer } from '@/components/feedback/drawer';
import { MultiSelectField, NumberField } from '@/components/forms';
import { ORDER_STATUS_CHIP } from '@/features/orders';
import { appTzLabel, fromDateRange, toDayjsRange } from '@/lib/date';

import { PAYMENT_METHOD_OPTIONS } from '../format';
import { orderHistoryToDefaults, orderHistoryToFilters } from '../order-history-filters';
import { orderHistoryFiltersSchema } from '../schemas';
import type { OrderHistoryFilterFormValues, OrderHistoryFilters } from '../types';

import styles from './customer-order-filter-drawer.module.scss';

const STATUS_OPTIONS = (Object.keys(ORDER_STATUS_CHIP) as (keyof typeof ORDER_STATUS_CHIP)[]).map(
  (value) => ({ value, label: ORDER_STATUS_CHIP[value].label }),
);

export type CustomerOrderFilterDrawerProps = {
  open: boolean;
  /** Last applied filters; the draft re-seeds from this on each open. */
  value: OrderHistoryFilters;
  onApply: (filters: OrderHistoryFilters) => void;
  onClose: () => void;
};

type BodyProps = Omit<CustomerOrderFilterDrawerProps, 'open'>;

function FilterBody({ value, onApply, onClose }: BodyProps) {
  // Date bounds are `enumField`s → `toDefaults` returns the applied value or `undefined`; coalesce
  // each to `null` so the field is controlled from mount WHILE preserving a re-seeded date (a
  // hardcoded `null` here would drop the applied Order/Delivery date range when the drawer reopens).
  const draft = orderHistoryToDefaults(value);
  const form = useForm<OrderHistoryFilterFormValues>({
    resolver: zodResolver(orderHistoryFiltersSchema),
    defaultValues: {
      ...draft,
      orderDateFrom: draft.orderDateFrom ?? null,
      orderDateTo: draft.orderDateTo ?? null,
      deliveryDateFrom: draft.deliveryDateFrom ?? null,
      deliveryDateTo: draft.deliveryDateTo ?? null,
    },
    mode: 'onBlur',
  });

  const handleApply = form.handleSubmit((values) => {
    onApply(orderHistoryToFilters(values));
    onClose();
  });

  // Two single range controls bound to their two draft fields each (keeps the To>=From validation
  // on orderDateTo/deliveryDateTo; Figma draws one field per range).
  const [orderDateFrom, orderDateTo] = useWatch({
    control: form.control,
    name: ['orderDateFrom', 'orderDateTo'],
  });
  const [deliveryDateFrom, deliveryDateTo] = useWatch({
    control: form.control,
    name: ['deliveryDateFrom', 'deliveryDateTo'],
  });
  const orderedError = form.formState.errors.orderDateTo?.message;
  const deliveredError = form.formState.errors.deliveryDateTo?.message;

  return (
    <Drawer
      open
      onClose={onClose}
      title="Filter"
      width={400}
      primaryText="Apply"
      onPrimary={handleApply}
      primaryDisabled={!form.formState.isValid}
      cancelText="Cancel"
      onCancel={onClose}
    >
      <fieldset className={styles.fieldset}>
        <legend className={styles.legend}>Order Amount</legend>
        <div className={styles.row}>
          <NumberField
            name="amountFrom"
            control={form.control}
            aria-label="Order Amount From"
            placeholder="From"
            prefix="$"
            min={0}
            precision={2}
            style={{ width: '100%' }}
          />
          <NumberField
            name="amountTo"
            control={form.control}
            aria-label="Order Amount To"
            placeholder="To"
            prefix="$"
            min={0}
            precision={2}
            style={{ width: '100%' }}
          />
        </div>
      </fieldset>

      <fieldset className={styles.fieldset}>
        <legend className={styles.legend}>{appTzLabel('Order Date Range')}</legend>
        <DateRangePicker
          aria-label={appTzLabel('Order Date Range')}
          format="MM/DD/YYYY"
          style={{ width: '100%' }}
          status={orderedError ? 'error' : undefined}
          value={toDayjsRange(orderDateFrom && orderDateTo ? [orderDateFrom, orderDateTo] : null)}
          onChange={(range) => {
            const iso = fromDateRange(range);
            form.setValue('orderDateFrom', iso?.[0] ?? null, { shouldValidate: true });
            form.setValue('orderDateTo', iso?.[1] ?? null, { shouldValidate: true });
          }}
        />
        {orderedError ? <p className={styles.error}>{orderedError}</p> : null}
      </fieldset>

      <fieldset className={styles.fieldset}>
        <legend className={styles.legend}>{appTzLabel('Delivery Date Range')}</legend>
        <DateRangePicker
          aria-label={appTzLabel('Delivery Date Range')}
          format="MM/DD/YYYY"
          style={{ width: '100%' }}
          status={deliveredError ? 'error' : undefined}
          value={toDayjsRange(
            deliveryDateFrom && deliveryDateTo ? [deliveryDateFrom, deliveryDateTo] : null,
          )}
          onChange={(range) => {
            const iso = fromDateRange(range);
            form.setValue('deliveryDateFrom', iso?.[0] ?? null, { shouldValidate: true });
            form.setValue('deliveryDateTo', iso?.[1] ?? null, { shouldValidate: true });
          }}
        />
        {deliveredError ? <p className={styles.error}>{deliveredError}</p> : null}
      </fieldset>

      <MultiSelectField
        name="orderStatus"
        control={form.control}
        label="Order Status"
        placeholder="Select"
        options={STATUS_OPTIONS}
      />

      <MultiSelectField
        name="paymentMethods"
        control={form.control}
        label="Payment Method"
        placeholder="Select"
        options={PAYMENT_METHOD_OPTIONS}
      />

      <fieldset className={styles.fieldset}>
        <legend className={styles.legend}>Number of Items</legend>
        <div className={styles.row}>
          <NumberField
            name="itemsFrom"
            control={form.control}
            aria-label="Number of Items From"
            placeholder="From"
            min={0}
            precision={0}
            style={{ width: '100%' }}
          />
          <NumberField
            name="itemsTo"
            control={form.control}
            aria-label="Number of Items To"
            placeholder="To"
            min={0}
            precision={0}
            style={{ width: '100%' }}
          />
        </div>
      </fieldset>
    </Drawer>
  );
}

export function CustomerOrderFilterDrawer({ open, ...rest }: CustomerOrderFilterDrawerProps) {
  if (!open) {
    return (
      <Drawer open={false} onClose={rest.onClose}>
        {null}
      </Drawer>
    );
  }
  // Unmount on close; remounts fresh on open so the draft re-seeds from the last applied `value`.
  return <FilterBody {...rest} />;
}
